import { Search, Mail, FileText, Mic, Sparkles, Briefcase } from "lucide-react";

const WebinarPointers = () => {
  const keyPointers = [
    {
      icon: <Search className="w-5 h-5 text-indigo-400" />,
      title: " How Founders Actually Hire for Their Office",
      description: "Hint: It's rarely through job portals.",
    },
    {
      icon: <Mail className="w-5 h-5 text-emerald-400" />,
      title: "DMs & Cold Emails That Get Replies",
      description: "Tried and tested scripts that got actual startup gigs.",
    },
    {
      icon: <FileText className="w-5 h-5 text-purple-400" />,
      title: "What Founders Look For in Your Resume",
      description:
        "Not big names. Not fluff. Just sharp, outcome-focused proof.",
    },
    {
      icon: <Mic className="w-5 h-5 text-pink-500" />,
      title: "Real Tools You Must Know",
      description:
        "From Notion to Loom — the unexpected skillset you must build.",
    },
    {
      icon: <Briefcase className="w-5 h-5 text-pink-500" />,
      title: "How to Sound Like a Business Generalist, Not Just a Fresher",
      description: `Crack the "Why Founder's Office?" convincingly in interviews.`,
    },
  ];

  return (
    <div className="bg-black py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/30 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
            <span className="text-sm font-semibold text-indigo-400 tracking-wide uppercase">
              What You'll Learn
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <h2 className="text-center text-3xl sm:text-4xl font-bold mb-8">
            <span className="bg-gradient-to-r from-white to-gray-500 bg-clip-text text-transparent">
              Learn What Most Students
            </span>
            <br />
            <span className="bg-gradient-to-r from-white to-gray-500 bg-clip-text text-transparent">
              Still Have No Clue About!
            </span>
          </h2>

          <div className="space-y-6">
            {/* First row - 2 cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {keyPointers.slice(0, 2).map((pointer, index) => (
                <div
                  key={index}
                  className="group relative bg-black border border-indigo-500/30 rounded-lg p-6 
                    hover:border-indigo-400 transition-all duration-300 
                    hover:bg-gradient-to-br hover:from-indigo-900/20 hover:to-blue-900/20"
                >
                  <div
                    className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-blue-500 
                        rounded-lg blur opacity-0 group-hover:opacity-30 
                        transition duration-300"
                  />

                  <div className="relative flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-indigo-500/10">
                      {pointer.icon}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-base text-left font-bold mb-2 text-white group-hover:text-indigo-300 transition-colors">
                        {pointer.title}
                      </h3>
                      <p className="text-gray-400 text-left text-sm group-hover:text-gray-200 transition-colors">
                        {pointer.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Second row - 2 cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {keyPointers.slice(2, 4).map((pointer, index) => (
                <div
                  key={index + 2}
                  className="group relative bg-black border border-indigo-500/30 rounded-lg p-6 
                    hover:border-indigo-400 transition-all duration-300 
                    hover:bg-gradient-to-br hover:from-indigo-900/20 hover:to-blue-900/20"
                >
                  <div
                    className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-blue-500 
                        rounded-lg blur opacity-0 group-hover:opacity-30 
                        transition duration-300"
                  />

                  <div className="relative flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-indigo-500/10">
                      {pointer.icon}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-base text-left font-bold mb-2 text-white group-hover:text-indigo-300 transition-colors">
                        {pointer.title}
                      </h3>
                      <p className="text-gray-400 text-left text-sm group-hover:text-gray-200 transition-colors">
                        {pointer.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Third row - 1 card centered */}
            <div className="flex justify-center">
              <div className="w-full md:w-1/2">
                <div
                  className="group relative bg-black border border-indigo-500/30 rounded-lg p-6 
                    hover:border-indigo-400 transition-all duration-300 
                    hover:bg-gradient-to-br hover:from-indigo-900/20 hover:to-blue-900/20"
                >
                  <div
                    className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-blue-500 
                        rounded-lg blur opacity-0 group-hover:opacity-30 
                        transition duration-300"
                  />

                  <div className="relative flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-indigo-500/10">
                      {keyPointers[4].icon}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-base text-left font-bold mb-2 text-white group-hover:text-indigo-300 transition-colors">
                        {keyPointers[4].title}
                      </h3>
                      <p className="text-gray-400 text-left text-sm group-hover:text-gray-200 transition-colors">
                        {keyPointers[4].description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              <span className="text-white font-semibold">
                Insights + Strategy
              </span>{" "}
              — A transformative session that goes beyond traditional advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarPointers;
